import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { User, KeyRound, Loader2, AlertCircle } from "lucide-react";
import { login } from "../actions/userActions";
import { FormInput } from "../components/FormInput";

const ErrorMessage = ({ message }) => (
	<div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 flex items-start gap-3">
		<AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
		<p>{message}</p>
	</div>
);

const LoginScreen = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");

	const userLogin = useSelector((state) => state.userLogin);
	const { loading, error, userInfo } = userLogin;

	let [searchParams] = useSearchParams();
	const redirect = searchParams.get("redirect");

	useEffect(() => {
		if (userInfo || userLogin?.isGuest) {
			if (!userLogin?.isGuest) {
				navigate("/");
			} else if (redirect) {
				navigate(redirect);
			}
		}
	}, [navigate, userInfo, redirect, userLogin?.isGuest]);

	const handleSubmit = (e) => {
		e.preventDefault();
		dispatch(login(email, password));
	};

	const error401 = error?.includes("401");

	return (
		<div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md mx-auto">
				<div className="text-center mb-8">
					<h2 className="text-3xl font-bold text-gray-900">Welcome back</h2>
					<p className="mt-2 text-gray-600">Please login in to your account</p>
				</div>

				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
					{error401 ? (
						<ErrorMessage message="Your email or password is incorrect. Please check again or reset password." />
					) : error ? (
						<ErrorMessage message={error} />
					) : null}

					<form onSubmit={handleSubmit} className="space-y-6 mt-6">
						<FormInput
							label="Email address"
							type="email"
							required
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="Enter your email"
						/>

						<FormInput
							label="Password"
							type="password"
							required
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder="Enter your password"
						/>

						<div className="flex items-center justify-between">
							<Link
								to="/account-recovery"
								className="text-sm text-primary hover:text-primary/80 transition-colors"
							>
								Forgot your password?
							</Link>
						</div>

						<button
							type="submit"
							disabled={loading}
							className="w-full px-4 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
						>
							{loading ? (
								<>
									<Loader2 className="w-5 h-5 animate-spin" />
									Signing in...
								</>
							) : (
								<>
									<KeyRound className="w-5 h-5" />
									Sign in
								</>
							)}
						</button>
					</form>

					<div className="mt-8 pt-6 border-t border-gray-200 text-center">
						<p className="text-gray-600">Don't have an account?</p>
						<Link
							to={redirect ? `/register?redirect=${redirect}` : "/register"}
							className="mt-4 w-full inline-flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors gap-2"
						>
							<User className="w-5 h-5" />
							Create an account
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
};

export default LoginScreen;
